import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { ApiResponse } from "../../../api/types/ApiResponse";
import { ApiResponseHandler } from "../handleApiResponse/handleApiResponse";
import handleRequestError from "../handleRequestError/handleRequestError";

/**
 * Deep envelope for repository backends.
 *
 * Runs a single HTTP call and folds the try / handleResponse /
 * success-or-error / catch plumbing that was copy-pasted across ~90 repository
 * methods into one place. Each method states only its endpoint call and DTO
 * mapper; the error plumbing lives here.
 *
 * Success requires a defined payload: a success code with a defined `data`
 * runs the mapper; a success code with no payload, an error code, or a thrown
 * transport error all yield an error result. This drops the scattered
 * `?? []` fallbacks — a "success" answer carrying no payload now surfaces
 * uniformly as an error instead of being silently coerced to an empty value.
 */
export async function executeRequest<Dto, T>(
  call: () => Promise<ApiResponse<Dto>>,
  map: (data: Dto) => T,
): Promise<RepositoryResult<T>> {
  try {
    const response = await call();
    const result = ApiResponseHandler.handleResponse<Dto>(response);
    if (result.success && result.data !== undefined) {
      return RepositoryResultFactory.success(map(result.data));
    }
    return RepositoryResultFactory.error(
      ApiResponseHandler.createExceptionFromResponse(response),
    );
  } catch (error) {
    return RepositoryResultFactory.error(
      handleRequestError(error as ApiResponse<unknown>),
    );
  }
}

/**
 * Void variant of {@link executeRequest} for operations with no payload.
 *
 * A success code yields `success(undefined)` regardless of any `data`; an
 * error code or a thrown transport error yields an error result.
 */
export async function executeVoidRequest<Dto>(
  call: () => Promise<ApiResponse<Dto>>,
): Promise<RepositoryResult<void>> {
  try {
    const response = await call();
    const result = ApiResponseHandler.handleResponse<Dto>(response);
    if (result.success) {
      return RepositoryResultFactory.success(undefined);
    }
    return RepositoryResultFactory.error(
      ApiResponseHandler.createExceptionFromResponse(response),
    );
  } catch (error) {
    return RepositoryResultFactory.error(
      handleRequestError(error as ApiResponse<unknown>),
    );
  }
}
